import {
  Box, Flex, Text, Input, Badge, InputGroup, InputLeftElement, Popover, PopoverTrigger, PopoverContent, Button, PopoverBody,
  useDisclosure, PopoverCloseButton, Center, Spinner, Image, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
} from "@chakra-ui/react";
import { SearchIcon, CalendarIcon } from "@chakra-ui/icons";
import Calendar from "react-calendar";
import { useState } from "react";
import { CustomTable, CustomToast } from "../../../components";
import SortIcon from "../../../assets/images/SortIcon.svg";
import { useFetch } from "../../../hooks/useFetch";
import { CustomPagination, FilterPopover } from "../../../components";
import { AttendanceReportRecord, AttendanceDayDetail, getStatusName, getStatusClass, sortOptions, sortMapping, Props, } from "./model";
import { showDate, showTime } from "../../../utilities/helper";
import MyDiv from "./attendanceReport.style";
import { Column } from "../../../components/CustomTable/customTable";

const AttendanceReportList = ({ reportData, dateRange, setDateRange, searchTerm, setSearchTerm,
  lazyParams, setLazyParams, loading, totalCount, pageCount, handlePageClick, setRowsPerPage, setSelectedItems, }: Props) => {
  const { isOpen: isSortOpen, onOpen: onSortOpen, onClose: onSortClose } = useDisclosure();
  const { isOpen: isCalOpen, onOpen: onCalOpen, onClose: onCalClose } = useDisclosure();

  const [selectingFrom, setSelectingFrom] = useState<"from" | "to">("from");
  const [selected, setSelected] = useState<string>(sortOptions[0]);
  const [detailModal, setDetailModal] = useState(false);
  const [selectedEmp, setSelectedEmp] = useState<AttendanceReportRecord | null>(null);
  const [activeStatus, setActiveStatus] = useState<number[] | null>(null);
  const [dayDetails, setDayDetails] = useState<AttendanceDayDetail[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const { addToast } = CustomToast();
  const { fetchApi } = useFetch(addToast);

  const handleSortSelect = (option: string) => {
    setSelected(option);
    const { field, order } = sortMapping[option];
    setLazyParams((prev: any) => ({ ...prev, sortField: field, sortOrder: order, page: 0 }));
    onSortClose();
  };

  const formatDate = (date: Date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

  const getStatusLabel = (statuses: number[] | null): string => {
    if (!statuses || statuses.length === 0) return "All";
    return statuses.map((s) => getStatusName(s)).join(" & ");
  };

  const handleCountClick = async (emp: AttendanceReportRecord, status: number[]) => {
    setSelectedEmp(emp);
    setActiveStatus(status);
    setDetailModal(true);
    setDayDetails([]);
    setDetailLoading(true);

    const params = new URLSearchParams({
      employeeId: String(emp.employeeId),
      fromDate: formatDate(dateRange.from),
      toDate: formatDate(dateRange.to),
    });
    status.forEach((s) => params.append("status", String(s)));

    const response = await fetchApi<AttendanceDayDetail[]>("Hrlense_Attendance/attendance-details", "GET", "", params.toString()
    );
    if (response) setDayDetails((response as any) ?? []);
    setDetailLoading(false);
  };

  const CountBadge = ({ count, status, emp, colorScheme }: {
    count: number;
    status: number[];
    emp: AttendanceReportRecord;
    colorScheme: string;
  }) => (
    <Badge colorScheme={colorScheme} variant="subtle" cursor="pointer" px={3} py={1}
      borderRadius="md" fontSize="sm" fontFamily="Poppins, sans-serif"
      onClick={() => handleCountClick(emp, status)}
      _hover={{ opacity: 0.8 }}
    >
      {count}
    </Badge>
  );

  return (
    <MyDiv>
      <Box className="page_wrapper">
        <Flex className="page_heading flex_header">
          <Text className="font-poppins text_medium text_lg">Attendance Report List</Text>

          <Flex className="filter_section" flexWrap="wrap" gap={2}>
            {/* Search */}
            <InputGroup maxW="210px">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input placeholder="Search Employee name" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </InputGroup>

            {/* Single Date Range Picker */}
            <Popover isOpen={isCalOpen} onOpen={() => { setSelectingFrom("from"); onCalOpen(); }} onClose={onCalClose} placement="bottom-end" closeOnBlur>
              <PopoverTrigger>
                <Button leftIcon={<CalendarIcon />} padding={2} variant="outline" size="sm">
                  <Text className="font-poppins text_sm text_regular">
                    {showDate(dateRange.from.toISOString())} → {showDate(dateRange.to.toISOString())}
                  </Text>
                </Button>
              </PopoverTrigger>
              <PopoverContent w="auto">
                <Box px={4} py={2} borderBottom="1px solid #eee" position="relative">
                  <Flex justify="center" gap={4} align="center">
                    <Text className="font-poppins text_sm"
                      fontWeight={selectingFrom === "from" ? "600" : "400"} color={selectingFrom === "from" ? "#006838" : "gray.500"}
                      cursor="pointer"
                      borderBottom={selectingFrom === "from" ? "2px solid #006838" : "2px solid transparent"}
                      pb={1} onClick={() => setSelectingFrom("from")}
                    >
                      From: {dateRange.from.toLocaleDateString()}
                    </Text>
                    <Text className="font-poppins text_sm" color="gray.400">→</Text>
                    <Text className="font-poppins text_sm"
                      fontWeight={selectingFrom === "to" ? "600" : "400"} color={selectingFrom === "to" ? "#006838" : "gray.500"}
                      cursor="pointer"
                      borderBottom={selectingFrom === "to" ? "2px solid #006838" : "2px solid transparent"}
                      pb={1} onClick={() => setSelectingFrom("to")}
                    >
                      To: {dateRange.to.toLocaleDateString()}
                    </Text>
                  </Flex>
                  <PopoverCloseButton top="8px" right="12px" />
                </Box>
                <PopoverBody p={2} display="flex" justifyContent="center">
                  {selectingFrom === "from" ? (
                    <Calendar value={dateRange.from}
                      onChange={(value) => { if (value instanceof Date) { setDateRange({ ...dateRange, from: value }); setSelectingFrom("to"); } }}
                      maxDate={dateRange.to}
                    />
                  ) : (
                    <Calendar value={dateRange.to}
                      onChange={(value) => { if (value instanceof Date) { setDateRange({ ...dateRange, to: value }); onCalClose(); setSelectingFrom("from"); } }}
                      minDate={dateRange.from} maxDate={new Date()}
                    />
                  )}
                </PopoverBody>
              </PopoverContent>
            </Popover>

            {/* Sort */}
            <Popover isOpen={isSortOpen} onOpen={onSortOpen} onClose={onSortClose} placement="bottom-end" closeOnBlur>
              <PopoverTrigger>
                <Image src={SortIcon} alt="Sort" className="icon_btn" />
              </PopoverTrigger>
              <PopoverContent w="270px" borderRadius="10px" boxShadow="md">
                <Box px={4} py={2} borderBottom="1px solid #eee">
                  <Text className="font-poppins text_sm text_medium" textAlign="center" color="#00000080">Sort By</Text>
                  <PopoverCloseButton top="8px" right="12px" />
                </Box>
                <PopoverBody p={0}>
                  {sortOptions.map((option) => (
                    <Box key={option} px={4} py={2} cursor="pointer"
                      bg={selected === option ? "#EBEBEB" : "white"}
                      _hover={{ bg: "#EBEBEB" }}
                      onClick={() => handleSortSelect(option)}
                    >
                      <Text className="font-poppins font_dark" fontSize="14px" fontWeight="500">{option}</Text>
                    </Box>
                  ))}
                </PopoverBody>
              </PopoverContent>
            </Popover>
          </Flex>
        </Flex>

        {/* Table */}
        <Box position="relative" minH="300px">
          {loading ? (
            <Center position="absolute" top={0} left={0} right={0} bottom={0}>
              <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="#006838" size="xl" />
            </Center>
          ) : (
            <CustomTable value={reportData?.records}>
              <Column header="S. No" body={(_: any, index: number) => index + 1} />
              <Column header="Emp. Code" body={(emp: AttendanceReportRecord) => (
                <Badge variant="subtle" className="empId font-poppins text_sm text_regular">{emp.employee_Code}</Badge>
              )} />
              <Column header="Employee Name" body={(emp: AttendanceReportRecord) => (
                <Text className="font-poppins text_sm text_medium">{emp.employeeName}</Text>
              )} />
              <Column header="Present" body={(emp: AttendanceReportRecord) => (
                <CountBadge count={emp.present} status={[1]} emp={emp} colorScheme="green" />
              )} />
              <Column header="Absent" body={(emp: AttendanceReportRecord) => (
                <CountBadge count={emp.absent} status={[0]} emp={emp} colorScheme="red" />
              )} />
              <Column header="Half Day" body={(emp: AttendanceReportRecord) => (
                <CountBadge count={emp.halfDay} status={[2]} emp={emp} colorScheme="orange" />
              )} />
              <Column header="Late " body={(emp: AttendanceReportRecord) => (
                <CountBadge count={emp.late} status={[3]} emp={emp} colorScheme="yellow" />
              )} />
              <Column header="Early Out " body={(emp: AttendanceReportRecord) => (
                <CountBadge count={emp.earlyOut} status={[4]} emp={emp} colorScheme="purple" />
              )} />
              <Column header="Week Off" body={(emp: AttendanceReportRecord) => (
                <CountBadge count={emp.weekOff} status={[7]} emp={emp} colorScheme="cyan" />
              )} />
                <Column header="Punch Missing" body={(emp: AttendanceReportRecord) => (
                <CountBadge count={emp.punchMissing} status={[5]} emp={emp} colorScheme="blue" />
              )} />
            </CustomTable>
          )}
        </Box>
      </Box>

      <CustomPagination pageCount={pageCount} rowsPerPageOptions={[20, 50, 200, 500]} handlePageClick={handlePageClick}
        setRowsPerPage={setRowsPerPage} rowsPerPage={lazyParams.rows} totalRecords={totalCount} currentPage={lazyParams.page}
      />

      <Modal isOpen={detailModal} onClose={() => { setDetailModal(false); setDayDetails([]); }} size="2xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader mt={4}>
            {selectedEmp?.employeeName} — {getStatusLabel(activeStatus)} Details
            <Text fontSize="sm" color="gray.500" fontWeight="normal" mt={1}>
              {showDate(dateRange.from.toDateString())} → {showDate(dateRange.to.toDateString())}
            </Text>
          </ModalHeader>
          <ModalBody pb={6}>
            {detailLoading ? (
              <Center py={8}>
                <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="#006838" size="xl" />
              </Center>
            ) : (
              <CustomTable value={dayDetails} loading={false} emptyMessage="No records found for this period.">
                <Column header="S. No" body={(_: any, index: number) => index + 1} />
                <Column header="Date" body={(row: AttendanceDayDetail) => showDate(row.date)} />

                {!activeStatus?.includes(0,) && !activeStatus?.includes(7) && (
                  <Column header="Check In" body={(row: AttendanceDayDetail) =>
                    !row.checkIn || row.checkIn === "00:00:00" ? "—" : showTime(row.checkIn)} />
                )}

                {!activeStatus?.includes(0,) && !activeStatus?.includes(7) && (
                  <Column header="Check Out" body={(row: AttendanceDayDetail) =>
                    row.checkOut ? showTime(row.checkOut) : "—"} />
                )}
                <Column header="Status" body={(row: AttendanceDayDetail) => (
                  <Text as="span" className={`font-poppins text_sm text_medium ${getStatusClass(row.status)}`}>
                    {getStatusName(row.status)}
                  </Text>
                )} />
                {!activeStatus?.includes(0,) && !activeStatus?.includes(7) && (
                  <Column header="Total Work Hour" body={(emp: AttendanceReportRecord) => (
                    <Text className="font-poppins text_sm text_medium">
                      {emp.totalWorkTime ?? "—"}
                    </Text>
                  )}
                  />
                )}
              </CustomTable>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </MyDiv>
  );
};

export default AttendanceReportList;
